/**
 * designerSnapshot — turn the Designer (IDE) view's on-screen state into a
 * human-readable SpaContext snapshot (2026-07-01, see+control generalization).
 *
 * Lets the voice ("BRIDGE") and Pi agents SEE which project is open, which
 * files are open in the editor, and which file is active — instead of guessing.
 * Pure + framework-free so it unit-tests directly (mirrors ghlSnapshot).
 */
import type { SpaContext } from '@/stores/spaContextStore';
import type { Project, OpenFile } from '@/types';

export const DESIGNER_VIEW_ID = 'designer';

function plural(n: number, one: string, many: string): string {
  return `${n} ${n === 1 ? one : many}`;
}

/** Build the on-screen snapshot for the Designer view. */
export function buildDesignerSnapshot(
  currentProject: Project | null,
  openFiles: OpenFile[],
  activeFileId: string | null,
): SpaContext {
  const active = openFiles.find((f) => f.id === activeFileId) ?? null;
  const items = openFiles.map((f) => {
    const parts = [f.path];
    if (f.language) parts.push(f.language);
    if (f.isModified) parts.push('modified');
    if (f.id === activeFileId) parts.push('← active');
    return parts.join(' · ');
  });

  let detail: string;
  if (!currentProject) {
    detail = 'No project open in the Designer';
  } else {
    const base = `Project "${currentProject.name}" (${currentProject.framework}); ${plural(
      openFiles.length,
      'open file',
      'open files',
    )}`;
    detail = active ? `${base}; editing "${active.path}"` : base;
  }

  return {
    view: DESIGNER_VIEW_ID,
    activeView: DESIGNER_VIEW_ID,
    title: currentProject ? `Designer — ${currentProject.name}` : 'Designer',
    detail,
    items,
  };
}
